import { SORT_FIELD_BY_TYPE } from '~/constants';
import { MarketplaceVendorsQueryVariables } from '~/gql/graphql';
import { MarketplaceSort } from '~/types/marketplace';

type GetVendorsSortQueryVariablesOptions = {
  categories?: string[];
  seed?: string;
};

// Random integer in [0, maxExclusive) using the platform CSPRNG. Avoids
// Math.random so static analysis isn't tripped (SonarQube S2245); the shuffle is
// only for vendor display distribution, not security, but the CSPRNG is free here.
const randomInt = (maxExclusive: number): number => {
  const [rand] = crypto.getRandomValues(new Uint32Array(1));
  return Math.floor((rand / 2 ** 32) * maxExclusive);
};

/**
 * Returns `count` vendors randomly drawn from `vendors` using a Fisher–Yates
 * shuffle over a copy of the input. Used to spread leads across the top-ranked
 * pool rather than always surfacing the same top vendors (INT-1041). The pick is
 * non-deterministic (fresh per call), so callers should memoise the result when a
 * stable order across re-renders is required.
 */
export const pickRandomVendors = <T>(vendors: T[], count: number): T[] => {
  const shuffled = [...vendors];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = randomInt(i + 1);
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, count);
};

export const getVendorsSortQueryVariables = (
  sortValue?: MarketplaceSort | null,
  options?: GetVendorsSortQueryVariablesOptions,
): Pick<MarketplaceVendorsQueryVariables, 'sort'> | undefined => {
  const { categories = [], seed } = options || {};

  if (!sortValue) {
    return undefined;
  }

  // NOTE: DEFAULT is deprecated; adopts the 'RANDOM' sorting value for backwards compatibility
  // with old feature flag
  const sortKey = sortValue === 'DEFAULT' ? 'RANDOM' : sortValue;
  const sort = sortKey ? SORT_FIELD_BY_TYPE[sortKey] : {};

  if (sortKey === 'RANDOM') {
    return {
      sort: {
        random: {
          seed,
        },
      },
    };
  }

  if (sort.type) {
    return {
      sort: {
        [sort.type]:
          sort.type === 'categoryPrice' && categories.length > 0
            ? {
                sortDirection: sort.direction,
                categorySlug: categories[0],
              }
            : sort.direction,
      },
    };
  }
};
